import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import CircularProgress from "@mui/material/CircularProgress";

import ImportSKUModal from "./ImportSKUModal";
import SearchItems from "./SearchItems";
import MoreOptionsMenu from "./MoreOptionsMenu";
import { useProject } from "../ProjectProvider";
import { useAssetsCollection } from "../../../hooks/capture/useAssetsCollection";
import { useAssetsFilterVariables } from "../../../hooks/capture/useAssetsFilterVariables";
import useAssetsFilter from "../../../hooks/capture/useAssetsFilter";
import useAssetsImport from "../../../hooks/useAssetsImport";
import useAuth from "../../../hooks/useAuth";
import ItemDetails from "./ItemDetails";
import {
  CREATE_SKU_MUTATION,
  EXPORT_EXCEL_DATA_MUTATION,
  UPDATE_PROJECT_MUTATION,
} from "../../../constants/graphql/mutations";
import useMutation from "../../../hooks/useMutation";
import { useBanner } from "../../../contexts/banner";
import { getProjectVariables } from "../../../utils/profile/settings";

const orderByKey = "sku_number";
const orderDirection = "desc";

const getSkuLibraryOrder = project => {
  const settings = project?.settings || {};
  const catalogPreferences = settings.catalog_preferences || {};
  const sortField = catalogPreferences.sku_library_sort || orderByKey;
  const sortDirection = catalogPreferences.sort === "asc" ? "asc" : orderDirection;

  return `${sortField}.${sortDirection}`;
};

export default function SKULibrary({ title }) {
  const { user } = useAuth();
  const { project } = useProject();
  const [selected, setSelected] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [order, setOrder] = useState(getSkuLibraryOrder(project));
  const variables = useAssetsFilterVariables(order);
  const { filter, search, setSearch, limit } = useAssetsFilter(variables, "Sku");
  const [{ data, loading, refetch }, { onLoadMore, fetchTotalIds }] = useAssetsCollection(filter);
  const [createSku, { loading: creating }] = useMutation(CREATE_SKU_MUTATION);
  const [updateProject] = useMutation(UPDATE_PROJECT_MUTATION);
  const [exportExcelData] = useMutation(EXPORT_EXCEL_DATA_MUTATION);
  const { displayBanner } = useBanner();
  const collection = data?.assetsCollection?.collection || [];
  const initialLoading = loading && collection.length === 0;

  useEffect(() => {
    setOrder(getSkuLibraryOrder(project));
  }, [
    project?.id,
    project?.settings?.catalog_preferences?.sku_library_sort,
    project?.settings?.catalog_preferences?.sort,
  ]);

  useAssetsImport(user?.id, () => {
    refetch();
  });

  const handleImportSKUs = () => {
    setImportModalOpen(true);
  };

  const handleCloseImportModal = () => {
    setImportModalOpen(false);
  };

  const updateReferenceAssets = () => {
    if (project?.referenceAssets) return;

    const variables = getProjectVariables({ ...project, referenceAssets: true });
    return updateProject({ variables });
  };

  const handleSetOrder = useCallback(
    nextOrder => {
      setOrder(nextOrder);

      if (!project?.id) return;

      const [nextSortField, nextSortDirectionValue] = nextOrder.split(".");
      const nextSortDirection = nextSortDirectionValue === "asc" ? "asc" : orderDirection;
      const currentCatalogPreferences = project?.settings?.catalog_preferences || {};

      if (
        currentCatalogPreferences.sku_library_sort === nextSortField &&
        currentCatalogPreferences.sort === nextSortDirection
      ) {
        return;
      }

      const variables = getProjectVariables({
        ...project,
        settings: {
          ...(project?.settings || {}),
          catalog_preferences: {
            ...currentCatalogPreferences,
            sku_library_sort: nextSortField,
            sort: nextSortDirection,
          },
        },
      });

      updateProject({ variables });
    },
    [project, updateProject]
  );

  const handleAddNewSKU = async () => {
    if (!project?.id) return;

    return createSku({
      variables: { projectId: project.id, input: {} },
      onSuccess: async ({ asset }) => {
        updateReferenceAssets();
        await refetch({ fetchPolicy: "network-only" });
        setSelected(asset);
      },
    });
  };

  const handleExportSKUs = () => {
    displayBanner({
      message: "Generating file...",
      type: "export-excel",
      projectId: project.id,
      startedAt: Date.now(),
    });
    exportExcelData({
      variables: { projectId: project.id, excludeReceivedAssets: true },
    });
  };

  return (
    <Paper sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {title}
      {initialLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
          <CircularProgress />
        </Box>
      ) : (
        <Box px={3} pb={2} pt={1} display="flex" flexDirection="column" flex={1} minHeight={0}>
          <Box display="flex" flexDirection="column" flex={1} minHeight={0}>
            <Box display="flex" justifyContent="center" py={1}>
              <Box
                component="img"
                borderRadius={3}
                maxWidth={140}
                maxHeight={110}
                src="/item-lookup.png"
              />
            </Box>
            {!search && collection.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Typography align="center" mb={1} maxWidth={450}>
                  Import the items you regularly receive or a procurement list to reference in the
                  Receiving Assistant.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleImportSKUs}
                  sx={{ my: 2 }}
                >
                  Import SKUs
                </Button>
              </Box>
            ) : (
              <>
                <Box display="flex" justifyContent="center">
                  <Typography align="center" variant="body1" noWrap>
                    Below is your SKU library. If you'd like to add more, use the "More Options" button
                  </Typography>
                </Box>
                <SearchItems
                  project={project}
                  order={order}
                  setOrder={handleSetOrder}
                  data={data}
                  loading={loading}
                  onLoadMore={onLoadMore}
                  search={search}
                  setSearch={setSearch}
                  limit={limit}
                  refetch={refetch}
                  selected={selected}
                  setSelected={setSelected}
                  fetchTotalIds={fetchTotalIds}
                  headerActions={
                    <MoreOptionsMenu
                      creating={creating}
                      onImportSKUs={handleImportSKUs}
                      onAddNewSKU={handleAddNewSKU}
                      onExportSKUs={handleExportSKUs}
                    />
                  }
                />
              </>
            )}
          </Box>
        </Box>
      )}
      {importModalOpen && (
        <ImportSKUModal
          open={importModalOpen}
          project={project}
          onClose={handleCloseImportModal}
          onAddNewSKU={handleAddNewSKU}
          updateReferenceAssets={updateReferenceAssets}
        />
      )}
      {selected && (
        <ItemDetails
          id={selected.id}
          project={project}
          onClose={() => setSelected(null)}
          refetch={refetch}
        />
      )}
    </Paper>
  );
}
